import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save, AlertTriangle } from 'lucide-react';
import {
  CreatePatientSchema,
  ENTIDADES_CATALOG,
  type CreatePatientInput,
} from '@datazo/contracts';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';

/**
 * Formulario reutilizable de paciente. Tanto el flujo "nuevo" como el de
 * "editar" comparten exactamente los mismos campos validados con
 * `CreatePatientSchema` — la diferencia está en la mutación y en los
 * `defaultValues`. La página decide el copy de cabecera y el callback
 * de submit; el componente solo valida y rinde la UI.
 */

export interface PatientFormProps {
  defaultValues: CreatePatientInput;
  submitLabel: string;
  submittingLabel: string;
  onSubmit(values: CreatePatientInput): Promise<void>;
  onCancel(): void;
  /** Mensaje a mostrar al pie del form (estado idle/error/ok). */
  feedback?:
    | { kind: 'idle' }
    | { kind: 'error'; message: string }
    | { kind: 'ok'; message: string };
  /** Si se especifica, deshabilita la edición del campo `documento`/`tipoDoc`. */
  lockIdentification?: boolean;
}

/**
 * Limpia strings vacíos de campos OPCIONALES antes de validar. Sin esto,
 * un input opcional dejado en blanco se envía como `''` y schemas que
 * exigen `.min(2)` o `.email()` rechazan el envío sin mostrar error —
 * el form quedaría aparentemente "bloqueado" al hacer click en Guardar.
 */
const OPTIONAL_STRING_FIELDS: ReadonlyArray<keyof CreatePatientInput> = [
  'telefono',
  'direccion',
  'ciudad',
  'eps',
  'historiaClinica',
  'alergias',
  'contactoEmergencia',
];

function sanitizeOptionalEmptyStrings(values: CreatePatientInput): CreatePatientInput {
  const cleaned: Record<string, unknown> = { ...values };
  for (const key of OPTIONAL_STRING_FIELDS) {
    const v = cleaned[key as string];
    if (typeof v === 'string' && v.trim() === '') {
      delete cleaned[key as string];
    }
  }
  return cleaned as CreatePatientInput;
}

export function PatientForm({
  defaultValues,
  submitLabel,
  submittingLabel,
  onSubmit,
  onCancel,
  feedback,
  lockIdentification,
}: PatientFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    control,
  } = useForm<CreatePatientInput>({
    // `mode: 'onSubmit'` y un resolver que recibe los valores ya limpios
    // (sin strings vacíos para campos opcionales) — evita que el form
    // quede en silencio cuando el usuario deja vacío un campo opcional
    // que en el schema tiene `.min()` o validación de formato.
    resolver: (values, ctx, opts) =>
      zodResolver(CreatePatientSchema)(sanitizeOptionalEmptyStrings(values), ctx, opts),
    defaultValues,
  });

  const tipoDoc = watch('tipoDoc');
  const sexo = watch('sexo');
  // Valor actual de la entidad. Si proviene de un registro previo con un valor
  // libre que no está en el catálogo, lo conservamos como opción extra para no
  // perderlo al editar.
  const entidadActual = watch('eps');
  const entidadFueraDeCatalogo =
    entidadActual !== undefined &&
    entidadActual !== '' &&
    !ENTIDADES_CATALOG.includes(entidadActual);

  const submit: SubmitHandler<CreatePatientInput> = async (values) => {
    await onSubmit(sanitizeOptionalEmptyStrings(values));
  };

  const isLocked = lockIdentification === true;

  return (
    <form onSubmit={(e) => void handleSubmit(submit)(e)} noValidate>
      <Card className="p-6 mb-4">
        <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
          <CardTitle>1. Identificación</CardTitle>
          <CardDescription>
            Documento + tipo es la clave única — no se permiten duplicados.
            {isLocked && ' Se bloquean tras la creación para preservar trazabilidad.'}
          </CardDescription>
        </CardHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>
              Tipo de documento <span className="text-danger">*</span>
            </Label>
            <select
              value={tipoDoc}
              disabled={isLocked}
              onChange={(e) =>
                setValue('tipoDoc', e.target.value as CreatePatientInput['tipoDoc'])
              }
              className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-surface-alt disabled:text-text-muted disabled:cursor-not-allowed"
            >
              <option value="CC">CC — Cédula de ciudadanía</option>
              <option value="CE">CE — Cédula de extranjería</option>
              <option value="TI">TI — Tarjeta de identidad</option>
              <option value="RC">RC — Registro civil</option>
              <option value="PA">PA — Pasaporte</option>
            </select>
          </div>

          <div className="flex flex-col gap-1.5 md:col-span-2">
            <Label htmlFor="documento">
              Número de documento <span className="text-danger">*</span>
            </Label>
            <Input
              id="documento"
              readOnly={isLocked}
              aria-invalid={errors.documento !== undefined}
              className={isLocked ? 'bg-surface-alt text-text-muted cursor-not-allowed' : ''}
              {...register('documento')}
            />
            {errors.documento?.message !== undefined && (
              <p className="text-[11.5px] text-danger">{errors.documento.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="nombres">
              Nombres <span className="text-danger">*</span>
            </Label>
            <Input
              id="nombres"
              aria-invalid={errors.nombres !== undefined}
              {...register('nombres')}
            />
            {errors.nombres?.message !== undefined && (
              <p className="text-[11.5px] text-danger">{errors.nombres.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5 md:col-span-2">
            <Label htmlFor="apellidos">
              Apellidos <span className="text-danger">*</span>
            </Label>
            <Input
              id="apellidos"
              aria-invalid={errors.apellidos !== undefined}
              {...register('apellidos')}
            />
            {errors.apellidos?.message !== undefined && (
              <p className="text-[11.5px] text-danger">{errors.apellidos.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fechaNacimiento">
              Fecha de nacimiento <span className="text-danger">*</span>
            </Label>
            <Input
              id="fechaNacimiento"
              type="date"
              aria-invalid={errors.fechaNacimiento !== undefined}
              {...register('fechaNacimiento')}
            />
            {errors.fechaNacimiento?.message !== undefined && (
              <p className="text-[11.5px] text-danger">{errors.fechaNacimiento.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>
              Sexo <span className="text-danger">*</span>
            </Label>
            <select
              value={sexo}
              onChange={(e) => setValue('sexo', e.target.value as CreatePatientInput['sexo'])}
              className="h-10 px-3 rounded-md border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="F">Femenino</option>
              <option value="M">Masculino</option>
              <option value="O">Otro / Prefiero no decir</option>
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="eps">Entidad</Label>
            <Controller
              control={control}
              name="eps"
              render={({ field }) => (
                <SearchableSelect
                  id="eps"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="— Selecciona la entidad —"
                  clearable
                  options={[
                    ...(entidadFueraDeCatalogo
                      ? [{ value: entidadActual, label: entidadActual }]
                      : []),
                    ...ENTIDADES_CATALOG.map((entidad) => ({ value: entidad, label: entidad })),
                  ]}
                />
              )}
            />
          </div>
        </div>
      </Card>

      <Card className="p-6 mb-4">
        <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
          <CardTitle>2. Información clínica</CardTitle>
          <CardDescription>
            Las alergias se muestran como alerta destacada en cada procedimiento.
          </CardDescription>
        </CardHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="alergias" className="flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-warning" /> Alergias conocidas
          </Label>
          <Input
            id="alergias"
            {...register('alergias')}
            placeholder='Ej. "Penicilina, Látex" — escribe "Ninguna" si no aplica'
          />
        </div>
      </Card>

      <Card className="p-6 mb-4">
        <CardHeader className="px-0 pt-0 pb-4 mb-4 border-b border-border">
          <CardTitle>3. Contacto y datos adicionales</CardTitle>
          <CardDescription>
            Teléfono, dirección y contacto de emergencia. Quedan guardados en el expediente y se
            reutilizan en los próximos procedimientos.
          </CardDescription>
        </CardHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="telefono">Teléfono</Label>
            <Input
              id="telefono"
              aria-invalid={errors.telefono !== undefined}
              {...register('telefono')}
              placeholder="Ej. 3001234567"
            />
            {errors.telefono?.message !== undefined && (
              <p className="text-[11.5px] text-danger">{errors.telefono.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ciudad">Ciudad</Label>
            <Input id="ciudad" {...register('ciudad')} placeholder="Ej. Barranquilla" />
          </div>

          <div className="flex flex-col gap-1.5 md:col-span-2">
            <Label htmlFor="direccion">Dirección</Label>
            <Input
              id="direccion"
              aria-invalid={errors.direccion !== undefined}
              {...register('direccion')}
              placeholder="Ej. Calle 84 # 45-12"
            />
            {errors.direccion?.message !== undefined && (
              <p className="text-[11.5px] text-danger">{errors.direccion.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="contactoEmergencia">Contacto de emergencia</Label>
            <Input
              id="contactoEmergencia"
              {...register('contactoEmergencia')}
              placeholder="Nombre y teléfono"
            />
          </div>
        </div>
      </Card>

      {feedback?.kind === 'error' && (
        <div className="mb-4 px-4 py-3 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200">
          {feedback.message}
        </div>
      )}
      {feedback?.kind === 'ok' && (
        <div className="mb-4 px-4 py-3 rounded-md bg-success-soft text-green-800 text-sm border border-green-200">
          {feedback.message}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          <Save className="w-4 h-4" />
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
}
